import React from 'react';
import { Link } from 'react-router-dom';
import { trackFreeTrial } from '../utils/tracking';

const About = () => {
  return (
    <>
      {/* Hero */}
      <section className="hero" style={{ paddingBottom: '40px' }}>
        <div className="paw-decoration" style={{ top: '15%', left: '5%' }}>🐾</div>
        <div className="paw-decoration" style={{ top: '25%', right: '8%', animationDelay: '1s' }}>🐾</div>

        <div className="container hero-content fade-up">
          <div className="section-badge purple">About Us</div>
          <h1 className="hero-title" style={{ fontSize: '2.75rem' }}>
            Built by People Who've <span className="text-gradient">Lived It</span>
          </h1>
          <p className="hero-subtitle">
            Over 40 years of combined experience in CRM, ATS, and revenue technology.
            We didn't build another all-in-one platform — we built the best-of-breed AI
            sourcing tool the industry has been missing.
          </p>

          <div className="hero-stats fade-up delay-3">
            <div className="hero-stat">
              <div className="hero-stat-value">40+</div>
              <div className="hero-stat-label">Years Combined Experience</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">100%</div>
              <div className="hero-stat-label">US-Based Team</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">0</div>
              <div className="hero-stat-label">Hidden Fees or Billables</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">100M+</div>
              <div className="hero-stat-label">Contact Records</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section style={{ paddingTop: 0 }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>

            {/* Mission Statement */}
            <div className="card" style={{ padding: '48px 40px', marginBottom: '32px', borderLeft: '4px solid var(--primary)' }}>
              <div className="section-badge blue" style={{ marginBottom: '16px' }}>Mission Statement</div>
              <p style={{ color: 'var(--text)', fontSize: '1.15rem', lineHeight: 1.9, fontWeight: 500 }}>
                Our mission is to empower revenue teams with intelligent, best-of-breed AI that
                works <em>with</em> their existing systems — not against them. We are committed to
                advancing sourcing, prospecting, and analytics through an open, agnostic approach
                that prioritizes accuracy, performance, and real business outcomes over
                one-size-fits-all platforms.
              </p>
            </div>

            {/* About kNine */}
            <div className="card" style={{ padding: '48px 40px', marginBottom: '32px' }}>
              <div className="section-badge purple" style={{ marginBottom: '16px' }}>About kNine</div>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '20px' }}>
                40+ Years of Industry Experience
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1.8, marginBottom: '16px' }}>
                At kNine, we bring more than 40 years of combined industry experience across the
                recruiting, CRM, and revenue technology landscape. Our team has worked inside some
                of the industry's largest platforms as well as the long-standing staples that have
                shaped the space.
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1.8, marginBottom: '16px' }}>
                That experience taught us something important:
              </p>

              <div style={{
                background: 'var(--off-white)',
                borderRadius: 'var(--radius-md)',
                padding: '24px 28px',
                marginBottom: '20px',
                borderLeft: '3px solid var(--accent)',
              }}>
                <p style={{ color: 'var(--text-dark)', fontSize: '1rem', lineHeight: 1.8, fontWeight: 600, marginBottom: '12px' }}>
                  Most "all-in-one" systems try to do everything — and end up doing many things
                  only moderately well.
                </p>
                <p style={{ color: 'var(--text-dark)', fontSize: '1rem', lineHeight: 1.8, fontWeight: 600 }}>
                  Revenue teams don't need another mediocre platform. They need intelligence that
                  strengthens the systems they already rely on.
                </p>
              </div>

              <p style={{ color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1.8 }}>
                That's exactly why we built kNine.
              </p>
            </div>

            {/* Meet Your AI Agents */}
            <div className="card" style={{ padding: '48px 40px', marginBottom: '32px' }}>
              <div className="section-badge green" style={{ marginBottom: '16px' }}>Meet The Pack</div>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '20px' }}>
                Cooper &amp; Roxi — Your AI Sourcing Companions
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1.8, marginBottom: '28px' }}>
                At the heart of kNine are two AI agents purpose-built for your revenue team.
                They learn your patterns, adapt to your workflows, and get smarter with every search.
              </p>

              <div className="feature-grid-2">
                <div className="card-flat" style={{ padding: '32px 28px', textAlign: 'center' }}>
                  <img
                    src="/images/Cooper.png"
                    alt="Cooper - BD & Sales AI Agent"
                    className="agent-mascot"
                    loading="lazy"
                  />
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '6px', color: 'var(--text-dark)' }}>
                    Cooper
                  </h3>
                  <div className="tag tag-blue" style={{ marginBottom: '12px' }}>BD &amp; Sales AI Agent</div>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7 }}>
                    Your business development AI agent. Cooper learns your sales playbook — the companies
                    you target, the decision-makers you need, and the data that closes deals.
                  </p>
                </div>
                <div className="card-flat" style={{ padding: '32px 28px', textAlign: 'center' }}>
                  <img
                    src="/images/roxi.png"
                    alt="Roxi - Recruiting AI Agent"
                    className="agent-mascot"
                    loading="lazy"
                  />
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '6px', color: 'var(--text-dark)' }}>
                    Roxi
                  </h3>
                  <div className="tag tag-purple" style={{ marginBottom: '12px' }}>Recruiting AI Agent</div>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7 }}>
                    Your recruiting AI agent. Roxi learns your hiring patterns — the roles you fill,
                    the talent you seek, and the sourcing strategies that work best for your firm.
                  </p>
                </div>
              </div>

              {/* Search & Detail Previews */}
              <div style={{ marginTop: '28px' }}>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.7, textAlign: 'center', marginBottom: '24px' }}>
                  From AI-powered search to enriched company intel and AI-generated candidate profiles
                  — everything pushes directly into your connected ATS or CRM.
                </p>

                {/* Cooper pair */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    <img src="/images/Cooper.png" alt="Cooper" style={{ width: '28px', height: '28px' }} />
                    <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-dark)' }}>Cooper</span>
                    <div className="tag tag-blue">Search &amp; Company Intel</div>
                  </div>
                  <div className="screenshot-grid-2">
                    <div className="screenshot-frame">
                      <div className="screenshot-bar">
                        <div className="screenshot-dot red"></div>
                        <div className="screenshot-dot yellow"></div>
                        <div className="screenshot-dot green"></div>
                        <div className="screenshot-bar-title">Cooper Search</div>
                      </div>
                      <img src="/images/cooper-search.jpg" alt="Cooper Search - AI-powered company search" loading="lazy" />
                    </div>
                    <div className="screenshot-frame">
                      <div className="screenshot-bar">
                        <div className="screenshot-dot red"></div>
                        <div className="screenshot-dot yellow"></div>
                        <div className="screenshot-dot green"></div>
                        <div className="screenshot-bar-title">Company Details</div>
                      </div>
                      <img src="/images/cooper-details.jpg" alt="Cooper company details with recommended contacts" loading="lazy" />
                    </div>
                  </div>
                </div>

                {/* Roxi pair */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    <img src="/images/roxi.png" alt="Roxi" style={{ width: '28px', height: '28px' }} />
                    <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-dark)' }}>Roxi</span>
                    <div className="tag tag-purple">Search &amp; AI Resume</div>
                  </div>
                  <div className="screenshot-grid-2">
                    <div className="screenshot-frame">
                      <div className="screenshot-bar">
                        <div className="screenshot-dot red"></div>
                        <div className="screenshot-dot yellow"></div>
                        <div className="screenshot-dot green"></div>
                        <div className="screenshot-bar-title">Roxi Search</div>
                      </div>
                      <img src="/images/roxi-search.jpg" alt="Roxi Search - AI-powered candidate sourcing" loading="lazy" />
                    </div>
                    <div className="screenshot-frame">
                      <div className="screenshot-bar">
                        <div className="screenshot-dot red"></div>
                        <div className="screenshot-dot yellow"></div>
                        <div className="screenshot-dot green"></div>
                        <div className="screenshot-bar-title">Candidate Profile</div>
                      </div>
                      <img src="/images/roxi-details.jpg" alt="Roxi candidate profile with AI resume and contact generation" loading="lazy" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Our Approach */}
            <div className="card" style={{ padding: '48px 40px', marginBottom: '32px' }}>
              <div className="section-badge green" style={{ marginBottom: '16px' }}>Our Approach</div>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '20px' }}>
                Platform-Agnostic by Design
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1.8, marginBottom: '16px' }}>
                kNine is intentionally platform-agnostic. We don't ask you to rip and replace
                your ATS or CRM. Instead, our AI layer connects directly to your existing database
                via secure APIs and learns from your real workflows, data patterns, and outcomes.
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1.8, marginBottom: '24px' }}>
                This allows us to deliver:
              </p>

              <div className="about-experience-grid">
                {[
                  { icon: '🎯', title: 'Smarter Sourcing & Prospecting', desc: 'Intelligent candidate and prospect discovery that learns your preferences and refines accuracy with every search.' },
                  { icon: '📈', title: 'Accurate Recommendations', desc: 'More precise lead and candidate recommendations powered by AI that understands your specific hiring and sales patterns.' },
                  { icon: '📊', title: 'Live Scoring & Forecasting', desc: 'Real-time performance scoring and revenue forecasting connected directly to your database — not stale exports or delayed syncs.' },
                  { icon: '🔬', title: 'Advanced Analytics', desc: 'Analytics capabilities that traditional systems simply cannot produce, giving your leadership real visibility into pipeline health.' },
                  { icon: '🧠', title: 'Continuous AI Learning', desc: 'An intelligence layer that improves over time, compounding in value as it learns from your sourcing activity and outcomes.' },
                  { icon: '🔗', title: 'Direct API Connections', desc: 'Secure integrations with your existing ATS, CRM, or database — Bullhorn, JobDiva, Salesforce, HubSpot, and more.' },
                ].map((item) => (
                  <div key={item.title} className="card-flat" style={{ padding: '20px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                    <div style={{ fontSize: '1.5rem', flexShrink: 0, marginTop: '2px' }}>{item.icon}</div>
                    <div>
                      <h4 style={{ fontWeight: 700, color: 'var(--text-dark)', fontSize: '0.95rem', marginBottom: '4px' }}>{item.title}</h4>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', lineHeight: 1.6 }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Analytics Preview */}
              <div style={{ marginTop: '28px' }}>
                <div className="screenshot-frame">
                  <div className="screenshot-bar">
                    <div className="screenshot-dot red"></div>
                    <div className="screenshot-dot yellow"></div>
                    <div className="screenshot-dot green"></div>
                    <div className="screenshot-bar-title">kNine Analytics Dashboard</div>
                  </div>
                  <img src="/images/analytics-3.png" alt="kNine Analytics Dashboard" loading="lazy" />
                </div>
              </div>

              <div className="card-flat" style={{
                marginTop: '24px',
                padding: '24px 28px',
                background: 'var(--primary-bg)',
                border: '1px solid #dbeafe',
                textAlign: 'center',
              }}>
                <p style={{ fontSize: '0.95rem', color: 'var(--text)', lineHeight: 1.7, maxWidth: '680px', margin: '0 auto' }}>
                  <strong style={{ color: 'var(--primary-dark)' }}>Connected intelligence, not isolated features.</strong>{' '}
                  Because the intelligence layer and your operational data work together, the system
                  evolves with your organization — not behind it.
                </p>
              </div>
            </div>

            {/* What We Believe */}
            <div className="card" style={{ padding: '48px 40px', marginBottom: '32px' }}>
              <div className="section-badge blue" style={{ marginBottom: '16px' }}>What We Believe</div>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '20px' }}>
                Best of Breed. Always.
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1.8, marginBottom: '16px' }}>
                We have no ambition to become another bloated, "do-everything" platform.
                Our focus is singular: to build and continuously advance the most effective AI
                intelligence layer for sourcing, lead generation, and revenue performance.
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1.8, marginBottom: '24px' }}>
                We reinvest directly into:
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px',
                marginBottom: '24px',
              }}>
                {[
                  { icon: '🧠', label: 'AI Model Improvement' },
                  { icon: '🔗', label: 'Deeper Platform Integrations' },
                  { icon: '⚡', label: 'Faster, More Accurate Analytics' },
                  { icon: '🤖', label: 'Expanded Automation Capabilities' },
                ].map((item) => (
                  <div key={item.label} className="card-flat" style={{
                    padding: '20px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                  }}>
                    <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>{item.icon}</span>
                    <span style={{ fontWeight: 600, color: 'var(--text-dark)', fontSize: '0.95rem' }}>{item.label}</span>
                  </div>
                ))}
              </div>

              <p style={{
                color: 'var(--text)',
                fontSize: '1rem',
                lineHeight: 1.8,
                fontWeight: 600,
                fontStyle: 'italic',
                textAlign: 'center',
                padding: '16px 24px',
                background: 'var(--off-white)',
                borderRadius: 'var(--radius-md)',
              }}>
                When our partners grow, we grow — and our roadmap reflects that.
              </p>
            </div>

            {/* Built Differently */}
            <div className="card" style={{ padding: '48px 40px', marginBottom: '32px' }}>
              <div className="section-badge purple" style={{ marginBottom: '16px' }}>Built Differently</div>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '20px' }}>
                True SaaS. Real Support. No Surprises.
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1.8, marginBottom: '24px' }}>
                kNine is a true SaaS platform, designed and supported entirely by our U.S.-based
                team. When you call for support, you're talking to us — not an outsourced call
                center, not a chatbot, not a third party. Everything is included because that's
                how it should be.
              </p>

              <div className="feature-grid-2">
                <div className="card-flat" style={{ padding: '24px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>🇺🇸</div>
                  <div>
                    <h4 style={{ fontWeight: 700, color: 'var(--text-dark)', fontSize: '0.95rem', marginBottom: '4px' }}>No Outsourced Support</h4>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', lineHeight: 1.7 }}>Every interaction is handled by our team in the United States.</p>
                  </div>
                </div>
                <div className="card-flat" style={{ padding: '24px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>⚡</div>
                  <div>
                    <h4 style={{ fontWeight: 700, color: 'var(--text-dark)', fontSize: '0.95rem', marginBottom: '4px' }}>No Surprise Billables</h4>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', lineHeight: 1.7 }}>Support, onboarding, training, and configuration — all included.</p>
                  </div>
                </div>
                <div className="card-flat" style={{ padding: '24px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>🤝</div>
                  <div>
                    <h4 style={{ fontWeight: 700, color: 'var(--text-dark)', fontSize: '0.95rem', marginBottom: '4px' }}>Direct Partnership Model</h4>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', lineHeight: 1.7 }}>Real relationships, not ticket queues. We grow when you grow.</p>
                  </div>
                </div>
                <div className="card-flat" style={{ padding: '24px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>🚀</div>
                  <div>
                    <h4 style={{ fontWeight: 700, color: 'var(--text-dark)', fontSize: '0.95rem', marginBottom: '4px' }}>Fast, Knowledgeable Help</h4>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', lineHeight: 1.7 }}>No scripts, no escalation chains. Real answers from real experts.</p>
                  </div>
                </div>
              </div>

              <div className="feature-grid-2" style={{ marginTop: '24px' }}>
                <div className="card-flat" style={{ padding: '28px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>
                    <span role="img" aria-label="office">🏢</span>
                  </div>
                  <h4 style={{ fontWeight: 700, color: 'var(--text-dark)', fontSize: '1.1rem', marginBottom: '4px' }}>
                    Cleveland, OH
                  </h4>
                  <p style={{ color: 'var(--primary)', fontSize: '0.9rem', fontWeight: 600 }}>Headquarters</p>
                </div>
                <div className="card-flat" style={{ padding: '28px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>
                    <span role="img" aria-label="office">🏢</span>
                  </div>
                  <h4 style={{ fontWeight: 700, color: 'var(--text-dark)', fontSize: '1.1rem', marginBottom: '4px' }}>
                    Raleigh, NC
                  </h4>
                  <p style={{ color: 'var(--primary)', fontSize: '0.9rem', fontWeight: 600 }}>East Coast Office</p>
                </div>
              </div>
            </div>

            {/* Industry Roots CTA */}
            <div className="cta-section" style={{ marginBottom: '32px' }}>
              <div className="cta-paw" style={{ top: '-20px', left: '-10px', transform: 'rotate(-25deg)' }}>🐾</div>
              <div className="cta-paw" style={{ bottom: '-20px', right: '-10px', transform: 'rotate(15deg)' }}>🐾</div>
              <h2 style={{ fontSize: '1.75rem' }}>We've Probably Already Met</h2>
              <p style={{ maxWidth: '600px' }}>
                If you've been in the recruiting or CRM industry over the past 25 years, there's
                a strong chance you've already crossed paths with someone on our team — at a
                conference, through a customer engagement, or during prior platform work.
                We'd love to reconnect and show you what we're building.
              </p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/contact" className="btn btn-white btn-lg" style={{ border: 'none' }}>
                  Schedule a Demo
                </Link>
                <a href="https://app.knine.ai/register" className="btn btn-lg" style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }} target="_blank" rel="noopener noreferrer" onClick={() => trackFreeTrial('about_cta')}>
                  Start Free Trial
                </a>
              </div>
            </div>

            {/* Contact Info */}
            <div className="card-flat" style={{ padding: '40px', background: 'var(--primary-bg)', border: '1px solid #dbeafe', textAlign: 'center' }}>
              <h3 style={{ fontWeight: 800, color: 'var(--primary-dark)', marginBottom: '12px', fontSize: '1.5rem' }}>
                Let's Talk
              </h3>
              <p style={{ fontSize: '1rem', color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: '20px', maxWidth: '560px', margin: '0 auto 20px' }}>
                We'd love to partner with you. Whether you want a live demo, have questions about
                integrations, or just want to talk shop — our team is here and ready.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', flexWrap: 'wrap', marginBottom: '24px' }}>
                <a href="tel:4404854497" style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--primary-dark)' }}>
                  (440) 485-4497
                </a>
                <a href="mailto:info@knine.ai" style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--primary-dark)' }}>
                  info@knine.ai
                </a>
              </div>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/contact" className="btn btn-primary btn-lg">Contact Us</Link>
                <a href="https://app.knine.ai/register" className="btn btn-outline btn-lg" target="_blank" rel="noopener noreferrer" onClick={() => trackFreeTrial('about_bottom')}>
                  Start Free Trial
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
